import { Field, FieldProps } from 'formik';
import React, { useState } from 'react';
import { Form, FormControlProps } from 'react-bootstrap';
import { ErrorI18n } from '../error';
import { Help } from '../help';
import { formatNumber } from '../../../utils/locale-utils';

interface CustomInputProps {
  controlId?: string;
  label?: string;
  className?: string;
  showValidation?: boolean;
  showError?: boolean;
  mapValue?: (value: string) => string;
  help?: string;
  inputClassName?: string;
  internal?: boolean;
}

export type InputProps = CustomInputProps &
  Omit<FormControlProps, 'as' | 'value'> &
  Pick<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'placeholder' | 'disabled' | 'name' | 'id'>;

export const Input = ({
  controlId,
  label,
  className = '',
  showValidation = true,
  showError = true,
  mapValue = (value) => value,
  required,
  help,
  inputClassName,
  type,
  ...additionalAttributes
}: InputProps) => {
  const [editingBigNumber, setEditingBigNumber] = useState(false);
  const handleBigNumberBlur = (event: any, field: any) => {
    field.onBlur(event);
    setEditingBigNumber(false);
  };
  const handleBigNumberFocus = () => {
    setEditingBigNumber(true);
  };

  return (
    <div className={`input${className ? ` ${className}` : ''}`}>
      <Field name={controlId}>
        {({ field, meta }: FieldProps) => {
          const valueToMap = field.value === null || field.value === undefined ? '' : field.value;
          const mappedValue = mapValue(valueToMap);

          return (
            <Form.Group controlId={controlId} className={required ? 'form-group required' : 'form-group'}>
              {label && <Form.Label>{label}</Form.Label>}
              {help && <Help placement="top">{help}</Help>}
              {type !== 'big-number' && (
                <Form.Control
                  value={mappedValue}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  isValid={showValidation && !meta.error && meta.touched}
                  isInvalid={showValidation && !!meta.error && meta.touched}
                  title={mappedValue}
                  className={inputClassName}
                  type={type}
                  {...additionalAttributes}
                />
              )}
              {type === 'big-number' && (
                <>
                  {editingBigNumber && (
                    <Form.Control
                      value={mappedValue}
                      onChange={field.onChange}
                      onBlur={(event: any) => handleBigNumberBlur(event, field)}
                      onWheel={(e: any) => e.target.blur()} // prevent modifying value when scrolling
                      isValid={showValidation && !meta.error && meta.touched}
                      isInvalid={showValidation && !!meta.error && meta.touched}
                      title={mappedValue}
                      className={inputClassName}
                      type="number"
                      autoFocus={true}
                      {...additionalAttributes}
                    />
                  )}
                  {!editingBigNumber && (
                    <Form.Control
                      value={formatNumber(field.value)}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isValid={showValidation && !meta.error && meta.touched}
                      isInvalid={showValidation && !!meta.error && meta.touched}
                      title={mappedValue}
                      className={inputClassName}
                      type="text"
                      onFocus={handleBigNumberFocus}
                      {...additionalAttributes}
                    />
                  )}
                </>
              )}
              {showError && <ErrorI18n {...meta} />}
            </Form.Group>
          );
        }}
      </Field>
    </div>
  );
};
